import Chevron from "@redotech/redo-icons/chevron-right_filled.svg";
import classNames from "classnames";
import { memo, useEffect, useRef, useState } from "react";
import * as css from "./accordion.module.css";
import { Flex } from "./flex";

interface AccordionProps {
  preview: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Applied to the clickable header row (preview + chevron). */
  headerClassName?: string;
  iconSize?: number;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export const Accordion = memo(function Accordion(props: AccordionProps) {
  const [isOpen, setIsOpen] = useState(props.defaultOpen ?? false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<string | number>("0px");

  const toggleAccordion = () => {
    if (props.disabled) {
      return;
    }
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    props.onToggle?.(newIsOpen);
  };

  useEffect(() => {
    const updateMaxHeight = () => {
      if (contentRef.current) {
        setMaxHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
      }
    };

    updateMaxHeight();

    const mutationObserver = new MutationObserver(() => {
      updateMaxHeight();
    });

    if (contentRef.current) {
      mutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (contentRef.current) {
        mutationObserver.disconnect();
      }
    };
  }, [isOpen, props.children]);

  return (
    <Flex
      align="center"
      className={classNames(css.accordion, props.className, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.open]: isOpen,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.closed]: !isOpen,
      })}
      dir="column"
    >
      <Flex
        className={classNames(css.header, props.headerClassName)}
        cursor={props.disabled ? "not-allowed" : "pointer"}
        justify="space-between"
        onClick={toggleAccordion}
        w="full"
      >
        <Flex align="center" justify="space-between" w="full">
          {props.preview}
          <Chevron
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            className={classNames(css.chevron, { [css.rotate]: isOpen })}
            style={{ width: props.iconSize }}
          />
        </Flex>
      </Flex>
      <Flex
        aria-expanded={isOpen}
        className={css.content}
        dir="column"
        inert={!isOpen}
        ref={contentRef}
        style={{ maxHeight }}
        w="full"
      >
        {props.children}
      </Flex>
    </Flex>
  );
});
